import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";

export const ENTITY_MEMBER_QUERY_MAXIMUM_LENGTH = 200;

/** The server's order fields for `GET legal-entities/{legalEntityId}/members`. */
export enum EntityMemberOrderField {
  Name = "Name",
  DateJoined = "DateJoined",
}

type EntityMemberQueryValues = {
  paginationParameters?: PaginationParameters;
  orderBy?: EntityMemberOrderField;
  orderDir?: OrderDirection;
  query?: string;
};

/**
 * What to ask the members endpoint for: a search, an order and a page. The server's defaults are
 * name, ascending, and the first page of 25.
 */
export default class EntityMemberQuery {
  public readonly paginationParameters: PaginationParameters;
  public readonly orderBy?: EntityMemberOrderField;
  public readonly orderDir?: OrderDirection;
  public readonly query?: string;

  public get searchParams(): URLSearchParams {
    return new URLSearchParams([...this.paginationParameters.searchParams, ...this.filterAndOrder]);
  }

  /** Every parameter except the page, so that the pages of one search share a key. */
  public get cacheKey(): string {
    return new URLSearchParams(this.filterAndOrder).toString();
  }

  public constructor(values?: EntityMemberQueryValues) {
    const paginationParameters = values?.paginationParameters ?? PaginationParameters.DEFAULT;
    if (
      !(paginationParameters instanceof PaginationParameters) ||
      !Number.isInteger(paginationParameters.pageIndex) ||
      paginationParameters.pageIndex < 0 ||
      !Number.isInteger(paginationParameters.pageSize) ||
      paginationParameters.pageSize < 1 ||
      paginationParameters.pageSize > 100
    ) {
      throw new Error("Valid pagination parameters are required.");
    }
    if (values?.orderBy && !Object.values(EntityMemberOrderField).includes(values.orderBy)) {
      throw new Error("A valid member order field is required.");
    }
    if (values?.orderDir && !Object.values(OrderDirection).includes(values.orderDir)) {
      throw new Error("A valid order direction is required.");
    }
    const query = values?.query?.trim() || undefined;
    if (query && query.length > ENTITY_MEMBER_QUERY_MAXIMUM_LENGTH) {
      throw new Error(`query cannot exceed ${ENTITY_MEMBER_QUERY_MAXIMUM_LENGTH} characters.`);
    }

    this.paginationParameters = paginationParameters;
    this.orderBy = values?.orderBy;
    this.orderDir = values?.orderDir;
    this.query = query;
  }

  public withPagination(paginationParameters: PaginationParameters): EntityMemberQuery {
    return new EntityMemberQuery({
      paginationParameters,
      orderBy: this.orderBy,
      orderDir: this.orderDir,
      query: this.query,
    });
  }

  private get filterAndOrder(): string[][] {
    return [
      ...(this.orderBy ? [["orderBy", this.orderBy]] : []),
      ...(this.orderDir ? [["orderDir", this.orderDir]] : []),
      ...(this.query ? [["query", this.query]] : []),
    ];
  }
}
